import { useEffect, useRef, useState } from 'react'
import { motion, useInView, useScroll, useTransform } from 'framer-motion'
import './App.css'

const sponsorTiers = [
  {
    name: 'Single Student',
    amount: 2000,
    students: 1,
    note: 'Give one student a full year of AI-powered learning tools.',
    impact: '12 months of access',
  },
  {
    name: 'Study Circle',
    amount: 10000,
    students: 5,
    note: 'Create a peer learning group with shared mentorship.',
    impact: 'Community support',
    featured: true,
  },
  {
    name: 'Full Cohort',
    amount: 50000,
    students: 25,
    note: 'Transform an entire classroom with structured AI access.',
    impact: 'Maximum leverage',
  },
]

const stats = [
  { value: 88, label: 'Lack AI Access', suffix: '%' },
  { value: 2000, label: 'Per Student/Year', prefix: '₹' },
  { value: 156, label: 'Students Waiting', suffix: '' },
]

const impactAreas = [
  { icon: '📝', title: 'Academic Writing', desc: 'Essays, research papers, application letters' },
  { icon: '🌐', title: 'Language Bridge', desc: 'Translation, comprehension, English fluency' },
  { icon: '💻', title: 'Technical Skills', desc: 'Coding, design, career-ready capabilities' },
  { icon: '🎯', title: 'Exam Preparation', desc: 'Entrance tests, competitive exams, interview prep' },
]

const trustPoints = [
  'Direct subscription funding — every rupee goes to student access',
  'Monthly recurring model ensures continuous learning, not one-time access',
  'Tied to Ambedkar Jayanti 2026 — honoring education as liberation',
  'Transparent allocation — see exactly which students receive support',
]

function AnimatedCounter({ end, duration = 2, prefix = '', suffix = '' }: {
  end: number
  duration?: number
  prefix?: string
  suffix?: string
}) {
  const [count, setCount] = useState(0)
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })

  useEffect(() => {
    if (!isInView) return

    let startTime: number
    let animationFrame: number

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp
      const progress = (timestamp - startTime) / (duration * 1000)

      if (progress < 1) {
        setCount(Math.floor(end * progress))
        animationFrame = requestAnimationFrame(animate)
      } else {
        setCount(end)
      }
    }

    animationFrame = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationFrame)
  }, [isInView, end, duration])

  return <span ref={ref}>{prefix}{count.toLocaleString('en-IN')}{suffix}</span>
}

function App() {
  const [progress] = useState(38) // Campaign progress percentage
  const heroRef = useRef(null)
  const { scrollYProgress } = useScroll()
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%'])

  useEffect(() => {
    document.title = 'Empower Students With AI | Ambedkar Jayanti Campaign'
  }, [])

  return (
    <main className="campaign-page">
      {/* Hero Section */}
      <section className="hero" ref={heroRef}>
        <motion.div
          className="hero__background"
          style={{ y }}
        >
          <div className="hero__gradient" />
          <div className="hero__pattern" />
        </motion.div>

        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hero__content"
          >
            <div className="hero__badge">
              <span className="badge-dot" />
              Ambedkar Jayanti 2026 • April 14
            </div>

            <h1 className="hero__title">
              <span className="title-line">Close The</span>
              <span className="title-line title-line--accent">AI Access Gap</span>
            </h1>

            <p className="hero__subtitle">
              Talent, ambition, and discipline shouldn't lose to a paywall.
              Fund AI subscriptions for students who can't afford them.
            </p>

            <div className="hero__actions">
              <a href="#tiers" className="btn btn--primary">
                <span>Start Sponsoring</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10h12m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a href="#impact" className="btn btn--secondary">
                See The Impact
              </a>
            </div>
          </motion.div>

          {/* Stats Grid */}
          <div className="stats-grid">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + i * 0.1 }}
                className="stat-card"
              >
                <div className="stat-value">
                  <AnimatedCounter
                    end={stat.value}
                    prefix={stat.prefix}
                    suffix={stat.suffix}
                  />
                </div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>

          {/* Progress Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="campaign-progress"
          >
            <div className="progress-header">
              <span className="progress-label">Campaign Progress</span>
              <span className="progress-percentage">{progress}% funded</span>
            </div>
            <div className="progress-bar">
              <motion.div
                className="progress-fill"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 1.5, delay: 1, ease: 'easeOut' }}
              />
            </div>
            <p className="progress-note">62 students still waiting for sponsorship</p>
          </motion.div>
        </div>

        <div className="hero__scroll-indicator">
          <div className="scroll-line" />
          <span>Scroll to explore</span>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="problem-section">
        <div className="container">
          <div className="problem-grid">
            <div className="problem-content">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <span className="section-label">The New Divide</span>
                <h2 className="section-title">
                  Privileged students already use AI.
                  <br />
                  <span className="text-accent">Others are locked out.</span>
                </h2>
                <p className="section-description">
                  ChatGPT, Claude, and other AI tools have become essential for
                  academic success. Students with access get help with essays,
                  coding, exam prep, and career planning. Those without fall behind
                  before the race even starts.
                </p>
              </motion.div>
            </div>

            <motion.div
              className="problem-visual"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="divide-card divide-card--have">
                <div className="divide-number">12%</div>
                <div className="divide-label">Have AI Access</div>
                <ul className="divide-list">
                  <li>Premium ChatGPT subscriptions</li>
                  <li>24/7 academic support</li>
                  <li>Competitive edge</li>
                </ul>
              </div>
              <div className="divide-separator">
                <div className="separator-line" />
                <span className="separator-label">vs</span>
                <div className="separator-line" />
              </div>
              <div className="divide-card divide-card--lack">
                <div className="divide-number">88%</div>
                <div className="divide-label">Cannot Afford It</div>
                <ul className="divide-list">
                  <li>Relying on outdated methods</li>
                  <li>Falling behind peers</li>
                  <li>Limited opportunities</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact Areas Section */}
      <section className="impact-section" id="impact">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <span className="section-label">What Students Gain</span>
            <h2 className="section-title">
              AI access translates into real academic power
            </h2>
          </motion.div>

          <div className="impact-grid">
            {impactAreas.map((area, i) => (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="impact-card"
              >
                <div className="impact-icon">{area.icon}</div>
                <h3 className="impact-title">{area.title}</h3>
                <p className="impact-desc">{area.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sponsorship Tiers Section */}
      <section className="tiers-section" id="tiers">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-header section-header--center"
          >
            <span className="section-label">Choose Your Impact</span>
            <h2 className="section-title">
              Monthly sponsorships that create lasting change
            </h2>
            <p className="section-description">
              Every tier funds recurring access, not one-time handouts.
              Students get continuous support throughout their academic journey.
            </p>
          </motion.div>

          <div className="tiers-grid">
            {sponsorTiers.map((tier, i) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                className={`tier-card ${tier.featured ? 'tier-card--featured' : ''}`}
              >
                {tier.featured && (
                  <div className="tier-badge">Most Popular</div>
                )}

                <div className="tier-header">
                  <div className="tier-students">
                    <div className="student-count">
                      <span className="count-number">{tier.students}</span>
                      <span className="count-label">
                        {tier.students === 1 ? 'Student' : 'Students'}
                      </span>
                    </div>
                  </div>
                  <h3 className="tier-name">{tier.name}</h3>
                  <p className="tier-note">{tier.note}</p>
                </div>

                <div className="tier-pricing">
                  <div className="tier-amount">
                    <span className="amount-currency">₹</span>
                    <span className="amount-value">
                      {tier.amount.toLocaleString('en-IN')}
                    </span>
                    <span className="amount-period">/month</span>
                  </div>
                  <div className="tier-impact">{tier.impact}</div>
                </div>

                <a href="#sponsor-form" className="tier-btn">
                  Sponsor Now
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10m0 0l-3-3m3 3l-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>

                <div className="tier-details">
                  <div className="detail-item">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M13.854 4.146a.5.5 0 010 .708l-7 7a.5.5 0 01-.708 0l-3-3a.5.5 0 11.708-.708L6.5 10.793l6.646-6.647a.5.5 0 01.708 0z"/>
                    </svg>
                    <span>ChatGPT Plus access</span>
                  </div>
                  <div className="detail-item">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M13.854 4.146a.5.5 0 010 .708l-7 7a.5.5 0 01-.708 0l-3-3a.5.5 0 11.708-.708L6.5 10.793l6.646-6.647a.5.5 0 01.708 0z"/>
                    </svg>
                    <span>Mentorship support</span>
                  </div>
                  <div className="detail-item">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M13.854 4.146a.5.5 0 010 .708l-7 7a.5.5 0 01-.708 0l-3-3a.5.5 0 11.708-.708L6.5 10.793l6.646-6.647a.5.5 0 01.708 0z"/>
                    </svg>
                    <span>Monthly progress reports</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="tiers-note"
          >
            <p>
              <strong>All contributions are tax-deductible.</strong> You'll receive monthly updates
              showing exactly how your sponsorship creates academic opportunity.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Trust & Transparency Section */}
      <section className="trust-section">
        <div className="container">
          <div className="trust-layout">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="trust-content"
            >
              <span className="section-label">How This Works</span>
              <h2 className="section-title">
                Direct funding.
                <br />
                Zero overhead claims.
                <br />
                <span className="text-accent">Full transparency.</span>
              </h2>
              <p className="section-description">
                Every rupee goes directly to student subscriptions. You'll receive
                monthly reports showing exactly which students received access and
                how they're using it academically.
              </p>

              <div className="trust-quote">
                <div className="quote-mark">"</div>
                <p>
                  If knowledge is power, then AI tools are now part of the
                  infrastructure of knowledge. Access cannot remain a privilege tax.
                </p>
                <div className="quote-attribution">
                  — Campaign Philosophy
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="trust-points"
            >
              {trustPoints.map((point, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="trust-point"
                >
                  <div className="trust-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <p>{point}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ambedkar Context Section */}
      <section className="context-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="context-card"
          >
            <div className="context-date">April 14, 2026</div>
            <h2 className="context-title">
              Honoring Ambedkar's vision of education as liberation
            </h2>
            <p className="context-text">
              Dr. B.R. Ambedkar believed education was the master key to social
              change. This campaign extends that vision into the AI age — ensuring
              that talent and ambition, not wealth, determine who gets to learn
              with the best tools available.
            </p>
            <div className="context-cta">
              <a href="#tiers" className="btn btn--primary">
                <span>Join The Movement</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10h12m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="cta-section" id="sponsor-form">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="cta-content"
          >
            <h2 className="cta-title">
              Ready to fund the next generation of learners?
            </h2>
            <p className="cta-description">
              Choose your sponsorship tier and start making an immediate impact.
              Set up monthly donations and receive regular updates on student progress.
            </p>

            <div className="cta-actions">
              <a href="#tiers" className="btn btn--primary btn--large">
                <span>Choose Sponsorship Tier</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10h12m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a href="mailto:info@nalanda-academy.org" className="btn btn--secondary btn--large">
                Contact Us Directly
              </a>
            </div>

            <div className="cta-note">
              Questions about the campaign? Want to sponsor more than 25 students?
              <a href="mailto:info@nalanda-academy.org"> Get in touch →</a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-info">
              <div className="footer-logo">Empower Students With AI</div>
              <p className="footer-tagline">
                Ambedkar Jayanti 2026 Campaign
              </p>
            </div>
            <div className="footer-links">
              <a href="mailto:info@nalanda-academy.org">Contact</a>
              <a href="#impact">Impact</a>
              <a href="#tiers">Sponsor</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2026 All rights reserved. Tax-deductible donations.</p>
          </div>
        </div>
      </footer>
    </main>
  )
}

export default App
